import { useState } from "react";

import { PageHeader } from "@/customer/shell/PageHeader";

import { CoiReviewForm } from "./CoiReviewForm";
import { CoiUploader } from "./CoiUploader";
import { useCoiUpload, useCurrentCoi, type CoiDocument, type CurrentCoiSummary } from "./api";

// Phase 2b coi-upload-and-review — `/account/coi` page. State machine
// (mirrors server status):
//   nothing uploaded      → <CoiUploader/>
//   processing            → spinner + "Reading your certificate…"
//   awaiting_customer_review → <CoiReviewForm/>
//   awaiting_admin_review | approved | rejected | expired | ocr_failed
//                          → status banner + (where applicable) "upload again"
//
// Audit P6 (`.notes/design-grades/portal/06-coi-upload.md`) — when an
// approved COI is already on file, show the reminder panel first and
// gate the uploader behind a "Replace" click so customers don't think
// they need to re-upload every visit. Expired COIs auto-reveal the
// uploader (no friction — they need a new one urgently).

export function CoiPage(): JSX.Element {
  const [activeId, setActiveId] = useState<number | null>(null);
  const [replacing, setReplacing] = useState(false);
  const { data, isLoading } = useCoiUpload(activeId);
  const currentQuery = useCurrentCoi();
  const current = currentQuery.data?.current ?? null;

  // Once the customer starts an upload, the in-progress flow takes
  // over — the current-COI reminder is irrelevant during that time.
  const showCurrent = activeId === null && current !== null;
  const showUploader = activeId === null && (current === null || current.expired || replacing);

  return (
    <section className="space-y-6">
      <PageHeader
        eyebrow="Insurance"
        title="Certificate of Insurance"
        description="We need a current ACORD 25 (or equivalent) on file before we can deliver equipment. Most rentals reuse the same certificate until its expiry date."
      />

      {showCurrent && (
        <CurrentCoiReminder
          coi={current}
          onReplace={() => setReplacing(true)}
        />
      )}

      {showUploader && <CoiUploader onUploaded={setActiveId} />}

      {activeId !== null && isLoading && <ProcessingPanel />}

      {activeId !== null && data && <StatusPanel doc={data} reset={() => { setActiveId(null); setReplacing(false); }} />}
    </section>
  );
}

// Audit P6 — "Your current COI is approved through DATE — re-upload
// only if your policy changed." The expired path is louder (danger
// tone) and auto-reveals the uploader; the approved path is calm and
// gates the uploader behind a Replace click.
function CurrentCoiReminder({
  coi,
  onReplace
}: {
  coi: CurrentCoiSummary;
  onReplace: () => void;
}): JSX.Element {
  const expired = coi.expired;
  const expiryLabel = coi.expiry_date ? formatLongDate(coi.expiry_date) : null;
  const tone = expired
    ? "rounded-md border border-danger/30 bg-danger/5 p-4 text-sm text-danger"
    : "rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-zinc-700";
  return (
    <div className={tone} data-testid="coi-current-reminder">
      <p className="font-semibold">
        {expired ? "Your COI has expired" : "Your current COI is approved"}
        {expiryLabel ? ` through ${expiryLabel}` : ""}
      </p>
      <p className="mt-1">
        {coi.insurer ?? "Insurer on file"}
        {coi.policy_number ? ` · Policy ${coi.policy_number}` : ""}
      </p>
      <div className="mt-3 flex items-center gap-3">
        <a
          href={coi.pdf_url}
          target="_blank"
          rel="noreferrer"
          className="text-sm font-medium text-brand-red hover:text-brand-red-dark"
        >
          View certificate
        </a>
        {!expired && (
          <button
            type="button"
            onClick={onReplace}
            className="text-sm font-medium text-zinc-700 hover:text-brand-red"
            data-testid="coi-replace-cta"
          >
            Replace
          </button>
        )}
      </div>
    </div>
  );
}

function formatLongDate(iso: string): string {
  // Parse a YYYY-MM-DD date as local-noon so timezones don't shift the
  // displayed day. Apr 23 stays Apr 23 in every TZ this app ships to.
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, (m ?? 1) - 1, d ?? 1, 12);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function ProcessingPanel(): JSX.Element {
  return (
    <div role="status" className="rounded-md border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-700">
      Loading your certificate…
    </div>
  );
}

function StatusPanel({ doc, reset }: { doc: CoiDocument; reset: () => void }): JSX.Element {
  if (doc.status === "processing") {
    return (
      <div role="status" className="rounded-md border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-700">
        Reading your certificate… this usually takes a few seconds.
      </div>
    );
  }
  if (doc.status === "awaiting_customer_review") {
    return <CoiReviewForm doc={doc} onSubmitted={reset} />;
  }
  if (doc.status === "awaiting_admin_review") {
    return (
      <InfoPanel
        title="We received your certificate"
        body="Our team will review it shortly and email you when it's approved (or if anything else is needed)."
      />
    );
  }
  if (doc.status === "approved") {
    return (
      <InfoPanel
        title="Approved"
        body="Your certificate is on file and good through its expiry date. You don't need to do anything further."
      />
    );
  }
  if (doc.status === "rejected" || doc.status === "ocr_failed") {
    return (
      <div className="space-y-3">
        <InfoPanel
          title={doc.status === "rejected" ? "Not approved" : "We couldn't read your certificate"}
          body="Please upload a new certificate."
          tone="danger"
        />
        <CoiUploader onUploaded={() => undefined} />
      </div>
    );
  }
  if (doc.status === "expired") {
    return (
      <InfoPanel
        title="Expired"
        body="The certificate on file has expired. Please upload a current one."
        tone="danger"
      />
    );
  }
  return <pre className="text-xs text-zinc-500">{JSON.stringify(doc, null, 2)}</pre>;
}

function InfoPanel({
  title,
  body,
  tone = "default"
}: {
  title: string;
  body: string;
  tone?: "default" | "danger";
}): JSX.Element {
  const cls =
    tone === "danger"
      ? "rounded-md border border-danger/30 bg-danger/5 p-4 text-sm text-danger"
      : "rounded-md border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-700";
  return (
    <div className={cls}>
      <p className="font-medium">{title}</p>
      <p className="mt-1">{body}</p>
    </div>
  );
}
